import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { trustedMerchantDirectUrl } from "./normalizeSource";

/** PDP preview overlay — opened only from source hover/click. */
export function SourcePreviewOverlay({ open, loading, error, data, source, onClose }) {
  const [zoom, setZoom] = React.useState(1);

  useEffect(() => {
    if (!open) return undefined;
    setZoom(1);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  // Never open Google / reference / google_product URLs from the overlay.
  const url = trustedMerchantDirectUrl(source);
  const screenshot = data?.imageUrl || data?.screenshot_url || data?.image_url;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] bg-navy-950/55 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="buy-source-preview-overlay"
    >
      <div
        className="bg-white border border-edge-subtle rounded-panel shadow-menu w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Source preview"
      >
        <header className="flex items-center gap-2 px-3 h-11 border-b border-edge-subtle shrink-0">
          <div className="min-w-0 flex-1 truncate text-compact font-semibold text-ink-primary">
            {source?.source_domain || source?.source_name || "Source preview"}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" onClick={() => setZoom(1)} className="h-8 px-2 rounded-ctrl text-micro border border-edge-subtle">
              Fit
            </button>
            <button type="button" onClick={() => setZoom(1)} className="h-8 px-2 rounded-ctrl text-micro border border-edge-subtle">
              100%
            </button>
            <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.15))} className="h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle" aria-label="Zoom out">
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={() => setZoom((z) => Math.min(2.5, z + 0.15))} className="h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle" aria-label="Zoom in">
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={() => setZoom(1)} className="h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle" aria-label="Reset zoom">
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle" aria-label="Open source">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
            <button type="button" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-ctrl hover:bg-surface-1" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto bg-surface-1 p-3">
          {loading ? (
            <div className="h-[420px] rounded-ctrl border border-dashed border-edge-subtle bg-surface-2 animate-pulse grid place-items-center text-compact text-ink-muted">
              Loading preview…
            </div>
          ) : error ? (
            <div className="h-[220px] rounded-ctrl border border-edge-subtle bg-white grid place-items-center text-compact text-ink-muted px-4 text-center">
              {error}
            </div>
          ) : data?.unavailable ? (
            <div className="h-[220px] rounded-ctrl border border-edge-subtle bg-white grid place-items-center text-compact text-ink-muted px-4 text-center">
              Preview token not available for this source yet.
            </div>
          ) : screenshot ? (
            <div className="overflow-auto max-h-[70vh]">
              <img
                src={screenshot}
                alt=""
                className="mx-auto origin-top transition-transform"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            </div>
          ) : (
            <div className="h-[220px] rounded-ctrl border border-edge-subtle bg-white grid place-items-center text-compact text-ink-muted">
              No preview image returned.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
